import { useRef, useCallback } from 'react';

/**
 * useSwipeGesture — lightweight touch swipe detection
 * @param {object} options
 * @param {function} options.onSwipeLeft  - called when user swipes left
 * @param {function} options.onSwipeRight - called when user swipes right
 * @param {number}   options.threshold   - min px to register as swipe (default 50)
 * @param {number}   options.velocityThreshold - min velocity px/ms (default 0.3)
 * @returns {{ ref, onTouchStart, onTouchEnd }} — spread onto the swipeable element
 */
const useSwipeGesture = ({
  onSwipeLeft,
  onSwipeRight,
  threshold = 50,
  velocityThreshold = 0.3,
} = {}) => {
  const touchStart = useRef(null);
  const touchStartTime = useRef(null);

  const onTouchStart = useCallback((e) => {
    const touch = e?.touches?.[0];
    if (!touch) return;
    touchStart.current = { x: touch?.clientX, y: touch?.clientY };
    touchStartTime.current = Date.now();
  }, []);

  const onTouchEnd = useCallback((e) => {
    if (!touchStart?.current) return;
    const touch = e?.changedTouches?.[0];
    if (!touch) return;

    const deltaX = touch?.clientX - touchStart?.current?.x;
    const deltaY = touch?.clientY - touchStart?.current?.y;
    const elapsed = Date.now() - touchStartTime?.current;
    const velocity = Math.abs(deltaX) / elapsed;

    // Ignore if vertical scroll dominates
    if (Math.abs(deltaY) > Math.abs(deltaX) * 1.5) {
      touchStart.current = null;
      return;
    }

    if (Math.abs(deltaX) >= threshold && velocity >= velocityThreshold) {
      if (deltaX < 0) {
        onSwipeLeft?.();
      } else {
        onSwipeRight?.();
      }
    }

    touchStart.current = null;
  }, [onSwipeLeft, onSwipeRight, threshold, velocityThreshold]);

  return { onTouchStart, onTouchEnd };
};

export default useSwipeGesture;
